import type { CascaderOption } from '@/components';
import type { QueryStoreQueryModel } from '@/features/search/_models/query-store';
import type { NestedOptionModel } from '@/utils';
import type { z } from 'zod';
import { create } from 'zustand';
import type { InputCascaderAndAccountingLabels } from '../_utils/projects-search';

type SearchQueryStoreState = {
  cascaderTargetIds: Pick<
    z.infer<typeof QueryStoreQueryModel>,
    | 'expenditureBudgetPurposeIds'
    | 'organizationIds'
    | 'otherResponsibleOrganizationIds'
    | 'policyIds'
  >;
  data: {
    organizationsOptions: CascaderOption[] | undefined;
    policiesOptions: CascaderOption[] | undefined;
    expenditureBudgetPurposesOptions:
      | z.infer<typeof NestedOptionModel>
      | undefined;
  };
  inputCascaderAndAccountingLabels: InputCascaderAndAccountingLabels;
  type: 'project' | 'group' | 'analysis';
  isLoading: boolean;
  openBasicAccordion: boolean;
  openAdvancedAccordion: boolean;
  openActivityIndicatorAccordion: boolean;
  // 事業一覧・グループ画面のみ
  openAdditionalAccordion: boolean;
  openAmountAccordion: boolean;
};

type SearchQueryStoreActions = {
  setCascaderTargetIds: (
    cascaderTargetIds: SearchQueryStoreState['cascaderTargetIds'],
  ) => void;
  setData: (data: SearchQueryStoreState['data']) => void;
  setInputCascaderAndAccountingLabels: (
    labels:
      | InputCascaderAndAccountingLabels
      | ((
          prev: InputCascaderAndAccountingLabels,
        ) => InputCascaderAndAccountingLabels),
  ) => void;
  reset: (type: SearchQueryStoreState['type']) => void;
  setIsLoading: (isLoading: SearchQueryStoreState['isLoading']) => void;
  setOpenBasicAccordion: (
    openBasicAccordion: SearchQueryStoreState['openBasicAccordion'],
  ) => void;
  setOpenAdvancedAccordion: (
    openAdvancedAccordion: SearchQueryStoreState['openAdvancedAccordion'],
  ) => void;
  setOpenActivityIndicatorAccordion: (
    openActivityIndicatorAccordion: SearchQueryStoreState['openActivityIndicatorAccordion'],
  ) => void;
  setOpenAmountAccordion: (
    openAmountAccordion: SearchQueryStoreState['openAmountAccordion'],
  ) => void;
  setOpenAdditionalAccordion: (
    openAdditionalAccordion: SearchQueryStoreState['openAdditionalAccordion'],
  ) => void;
};

const initialState: SearchQueryStoreState = {
  cascaderTargetIds: {
    expenditureBudgetPurposeIds: null,
    organizationIds: null,
    otherResponsibleOrganizationIds: null,
    policyIds: null,
  },
  data: {
    organizationsOptions: undefined,
    policiesOptions: undefined,
    expenditureBudgetPurposesOptions: undefined,
  },
  inputCascaderAndAccountingLabels: {
    nestedOrganizationIds: [],
    nestedOtherResponsibleOrganizationIds: [],
    nestedPolicyIds: [],
    nestedExpenditureBudgetPurposeIds: [],
    accountingTypeIds: [],
  },
  type: 'project',
  isLoading: true,
  openBasicAccordion: false,
  openAdvancedAccordion: false,
  openActivityIndicatorAccordion: false,
  openAmountAccordion: false,
  openAdditionalAccordion: false,
};

export const useProjectsSearchStore = create<
  SearchQueryStoreState & SearchQueryStoreActions
>((set) => ({
  ...initialState,
  setCascaderTargetIds: (cascaderTargetIds) => set({ cascaderTargetIds }),
  setData: (data) => set({ data }),
  setInputCascaderAndAccountingLabels: (labels) =>
    set((state) => ({
      inputCascaderAndAccountingLabels:
        typeof labels === 'function'
          ? labels(state.inputCascaderAndAccountingLabels)
          : labels,
    })),
  reset: (type) => set({ ...initialState, type }),
  setIsLoading: (isLoading) => set({ isLoading }),
  setOpenBasicAccordion: (openBasicAccordion) => set({ openBasicAccordion }),
  setOpenAdvancedAccordion: (openAdvancedAccordion) =>
    set({ openAdvancedAccordion }),
  setOpenActivityIndicatorAccordion: (openActivityIndicatorAccordion) =>
    set({ openActivityIndicatorAccordion }),
  setOpenAmountAccordion: (openAmountAccordion) => set({ openAmountAccordion }),
  setOpenAdditionalAccordion: (openAdditionalAccordion) =>
    set({ openAdditionalAccordion }),
}));
